// 組成(部位/組成/サブ組成/混率)グリッドの列メタ解決と混率自動計算をまとめる。
// 加工指示・品番マスタで同一のロジックのため共有する。
export function createSoseiCalcService(deps) {
    const {
        isEmpty,
        DicVal,
        DeepCopy,
        normalizeText,
        getColumnInfo,
        fnc
    } = deps;

    function normalizeSoseiColumnId(value) {
        return normalizeText(value).toUpperCase();
    }

    // パネル type に紐づく列定義から、組成計算に必要な列IDと桁数を解決する。
    function getSoseiPanelColumnInfo(panelInfo) {
        const columninfo = getColumnInfo();
        const panelType = normalizeText(DicVal(panelInfo, 'type', ''));
        const cols = Array.isArray(columninfo)
            ? columninfo.filter((c) => normalizeText(DicVal(c, 'type', '')) === panelType)
            : [];
        const findById = (ids) => {
            const uppers = ids.map(normalizeSoseiColumnId);
            return cols.find((c) => uppers.includes(normalizeSoseiColumnId(DicVal(c, 'columnid', '')))) || null;
        };
        const findByDataId = (dataid) => cols.find((c) => {
            const target = normalizeSoseiColumnId(dataid);
            return [
                DicVal(c, 'dataid', ''),
                DicVal(c, 'mastertype', ''),
                DicVal(c, 'dataidctrl', '')
            ].some((value) => normalizeSoseiColumnId(value) === target);
        }) || null;
        const findByName = (predicate) => cols.find((c) => predicate(
            normalizeText(DicVal(c, 'columnnm', '')),
            normalizeSoseiColumnId(DicVal(c, 'columnid', '')),
            normalizeSoseiColumnId(DicVal(c, 'table', ''))
        )) || null;
        const partColInfo = findById(['SSDQ_BUICD', 'SSDH_BUICD'])
            || findByName((name, id) => name.indexOf('部位') !== -1 || id.indexOf('BUICD') !== -1);
        const mainColInfo = findById(['SSDQ_SOSEICD', 'SSDH_SOSEICD'])
            || findByDataId('M_SOS')
            || findByName((name, id) => id.indexOf('SOSEICD') !== -1 && id.indexOf('SUB') === -1 && name.indexOf('サブ') === -1);
        const mainRateColInfo = findById(['SSDQ_KONRITU'])
            || findByName((name, id) => name === '混率' || (id.indexOf('KONRITU') !== -1 && id.indexOf('SUB') === -1));
        const subColInfo = findById(['SSDQ_SUB_SOSEICD', 'SSDQNR_SUBSOSEICD1'])
            || findByDataId('M_SSOS')
            || findByName((name, id) => (id.indexOf('SUB') !== -1 && id.indexOf('SOSEI') !== -1) || (name.indexOf('サブ組成') !== -1 && name.indexOf('混率') === -1));
        const subNameColInfo = findById(['SSOS_NAME', 'SSDQNR_SUBSOSEI_NM1'])
            || cols.find((c) => normalizeSoseiColumnId(DicVal(c, 'table', '')) === 'M_SSOS' && normalizeSoseiColumnId(DicVal(c, 'columnid', '')).indexOf('NAME') !== -1)
            || null;
        const subRateColInfo = findById(['SSDQ_SUB_KONRITU'])
            || findByName((name, id) => (id.indexOf('SUB') !== -1 && id.indexOf('KONRITU') !== -1) || name.indexOf('サブ組成混率') !== -1);
        const pickMetaText = (info, keys) => {
            for (let i = 0; i < keys.length; i++) {
                const value = normalizeText(DicVal(info, keys[i], ''));
                if (value !== '') return value;
            }
            return '';
        };
        return {
            partCol: normalizeText(DicVal(partColInfo, 'columnid', '')),
            mainCol: normalizeText(DicVal(mainColInfo, 'columnid', '')),
            mainDataId: pickMetaText(mainColInfo, ['dataid', 'mastertype', 'dataidctrl']),
            mainDbColumn: pickMetaText(mainColInfo, ['dbcolumn', 'dbcolumnnm']),
            mainRateCol: normalizeText(DicVal(mainRateColInfo, 'columnid', '')),
            subCol: normalizeText(DicVal(subColInfo, 'columnid', '')),
            subNameCol: normalizeText(DicVal(subNameColInfo, 'columnid', '')),
            subRateCol: normalizeText(DicVal(subRateColInfo, 'columnid', '')),
            subRateDecimalPlaces: Number(DicVal(subRateColInfo, 'decimalplace', DicVal(subRateColInfo, 'decimalplaces', 0))) || 0
        };
    }
    function readSoseiRowValue(row, field) {
        if (isEmpty(row) || isEmpty(field)) return '';
        const direct = DicVal(row, field, null);
        if (direct !== null && direct !== undefined) {
            const directText = normalizeText(direct);
            if (directText !== '') return directText;
        }
        const cd = DicVal(row, `${field}_cd`, DicVal(row, `${field}_CD`, null));
        if (cd !== null && cd !== undefined) return normalizeText(cd);
        return '';
    }
    function readSoseiRowName(row, codeField, nameField) {
        if (isEmpty(row)) return '';
        const code = !isEmpty(codeField) ? readSoseiRowValue(row, codeField) : '';
        const fromCodeName = !isEmpty(codeField)
            ? normalizeText(DicVal(row, `${codeField}_nm`, DicVal(row, `${codeField}_NM`, '')))
            : '';
        if (fromCodeName !== '' && fromCodeName !== code) return fromCodeName;
        const fromNameField = !isEmpty(nameField) ? readSoseiRowValue(row, nameField) : '';
        if (fromNameField !== '' && fromNameField !== code) return fromNameField;
        return '';
    }
    function writeSoseiRowField(row, field, value) {
        if (isEmpty(row) || isEmpty(field)) return;
        row[field] = value;
    }
    function writeSoseiMasterValue(row, codeField, nameField, code, name) {
        if (isEmpty(row)) return;
        const nextCode = normalizeText(code);
        const nextName = normalizeText(name);
        if (!isEmpty(codeField)) {
            row[codeField] = nextCode;
            row[`${codeField}_cd`] = nextCode;
            row[`${codeField}_CD`] = nextCode;
            if (nextName !== '') {
                row[`${codeField}_nm`] = nextName;
                row[`${codeField}_NM`] = nextName;
            }
        }
        if (!isEmpty(nameField)) {
            row[nameField] = nextName;
        }
    }
    function parseSoseiRate(value) {
        const raw = normalizeText(value).replace(/,/g, '').replace(/%/g, '');
        if (raw === '') return null;
        const num = Number(raw);
        if (!Number.isFinite(num)) return null;
        return num;
    }
    function isBlankSoseiRate(row, rateField) {
        const rate = parseSoseiRate(readSoseiRowValue(row, rateField));
        return rate === null || rate === 0;
    }
    function isBlankSoseiSubFiber(row, meta) {
        return normalizeText(DicVal(row, meta.subCol, '')) === '';
    }
    function isBlankSoseiDisplayValue(row, field) {
        if (isEmpty(field)) return true;
        const value = normalizeText(DicVal(row, field, ''));
        return value === '' || value === '-';
    }
    function isBlankSoseiDisplayRow(row, meta) {
        if (isEmpty(row)) return true;
        const fields = [meta.partCol, meta.mainCol, meta.mainRateCol, meta.subCol, meta.subNameCol, meta.subRateCol].filter((field) => !isEmpty(field));
        if (fields.length === 0) return false;
        return fields.every((field) => isBlankSoseiDisplayValue(row, field));
    }
    function isSoseiRemainderCandidateRow(row, meta) {
        if (isEmpty(row)) return true;
        const fields = [meta.partCol, meta.mainRateCol, meta.subRateCol].filter((field) => !isEmpty(field));
        if (fields.length === 0) return false;
        return fields.every((field) => isBlankSoseiDisplayValue(row, field));
    }
    function formatSoseiRate(value, decimalPlaces) {
        const scale = Math.pow(10, Math.max(0, decimalPlaces || 0));
        const rounded = Math.round((Number(value) || 0) * scale) / scale;
        if ((decimalPlaces || 0) <= 0) return String(Math.round(rounded));
        return rounded.toFixed(decimalPlaces).replace(/\.?0+$/, '');
    }
    // 残りの混率を等分し、端数は先頭行に寄せる。
    function splitSoseiRate(total, count, decimalPlaces) {
        if (count <= 0) return [];
        const scale = Math.pow(10, Math.max(0, decimalPlaces || 0));
        const totalUnits = Math.max(0, Math.round((Number(total) || 0) * scale));
        const baseUnits = Math.floor(totalUnits / count);
        const remainder = totalUnits - (baseUnits * count);
        return Array.from({ length: count }, (_, index) => (baseUnits + (index === 0 ? remainder : 0)) / scale);
    }
    function readSoseiMasterCacheName(dataid, code, dbcolumn) {
        try {
            const targetDataId = normalizeText(dataid);
            const targetCode = normalizeText(code);
            if (targetDataId === '' || targetCode === '' || isEmpty(fnc.current) || typeof fnc.current.getMaster !== 'function') return '';
            const data = fnc.current.getMaster(targetDataId, targetCode, '');
            if (isEmpty(data)) return '';
            const keys = [dbcolumn, 'name', 'SOS_NAMERNM', 'SOS_NAME', 'SSOS_NAMERNM', 'SSOS_NAME', 'm_code_name'].filter((v) => normalizeText(v) !== '');
            for (let i = 0; i < keys.length; i++) {
                const value = normalizeText(DicVal(data, keys[i], ''));
                if (value !== '' && value !== targetCode) return value;
            }
        } catch (e) { console.error(e); }
        return '';
    }
    function resolveDefaultSubSosei(row, meta) {
        const mainCode = readSoseiRowValue(row, meta.mainCol);
        const parentName = readSoseiRowName(row, meta.mainCol, '') || readSoseiMasterCacheName(meta.mainDataId, mainCode, meta.mainDbColumn);
        return { code: mainCode, name: parentName };
    }
    function applyDefaultSubSoseiToRow(row, meta, defaultSub, rate) {
        if (isBlankSoseiSubFiber(row, meta)) {
            writeSoseiMasterValue(row, meta.subCol, meta.subNameCol, defaultSub.code, defaultSub.name);
        }
        writeSoseiRowField(row, meta.subRateCol, formatSoseiRate(rate, meta.subRateDecimalPlaces));
    }
    function addSoseiRateToRow(row, meta, addRate) {
        const currentRate = parseSoseiRate(readSoseiRowValue(row, meta.subRateCol)) || 0;
        writeSoseiRowField(row, meta.subRateCol, formatSoseiRate(currentRate + (Number(addRate) || 0), meta.subRateDecimalPlaces));
    }
    function sameSoseiParentIdentity(left, right, meta) {
        if (isEmpty(left) || isEmpty(right)) return false;
        const keys = [meta.partCol, meta.mainCol, meta.mainRateCol].filter((v) => !isEmpty(v));
        if (keys.length === 0) return false;
        return keys.every((key) => readSoseiRowValue(left, key) === readSoseiRowValue(right, key));
    }
    // 入力順のまま、組成コードが入った行を親としてグループ化する。
    function buildSoseiGroupsByInputOrder(rows, meta) {
        const groups = [];
        let current = null;
        rows.forEach((row) => {
            const mainCode = readSoseiRowValue(row, meta.mainCol);
            if (mainCode !== '') {
                const repeatsCurrentParent = !isEmpty(current) && sameSoseiParentIdentity(row, current.headerRow, meta);
                if (!repeatsCurrentParent) {
                    current = { headerRow: row, rows: [] };
                    groups.push(current);
                }
            }
            if (!isEmpty(current)) current.rows.push(row);
        });
        return groups;
    }
    function hasDuplicateSoseiFiberRows(rows, meta) {
        const partCol = normalizeText(DicVal(meta, 'partCol', '')) || 'SSDQ_BUICD';
        const mainCol = normalizeText(DicVal(meta, 'mainCol', '')) || 'SSDQ_SOSEICD';
        const subCol = normalizeText(DicVal(meta, 'subCol', '')) || 'SSDQ_SUB_SOSEICD';
        const seen = new Set();
        return (Array.isArray(rows) ? rows : []).some((row) => {
            const partCode = readSoseiRowValue(row, partCol);
            const mainCode = readSoseiRowValue(row, mainCol);
            const subCode = readSoseiRowValue(row, subCol);
            if (partCode === '' || mainCode === '' || subCode === '') return false;
            const key = [partCode, mainCode, subCode].join('\u001f');
            if (seen.has(key)) return true;
            seen.add(key);
            return false;
        });
    }
    function hasSoseiRateOverLimit(rows, meta) {
        const partCol = normalizeText(DicVal(meta, 'partCol', '')) || 'SSDQ_BUICD';
        const mainCol = normalizeText(DicVal(meta, 'mainCol', '')) || 'SSDQ_SOSEICD';
        const subCol = normalizeText(DicVal(meta, 'subCol', '')) || 'SSDQ_SUB_SOSEICD';
        const mainRateCol = normalizeText(DicVal(meta, 'mainRateCol', '')) || 'SSDQ_KONRITU';
        const subRateCol = normalizeText(DicVal(meta, 'subRateCol', '')) || 'SSDQ_SUB_KONRITU';
        const partTotals = new Map();
        const subTotals = new Map();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const partCode = readSoseiRowValue(row, partCol);
            if (partCode !== '') {
                const mainRate = parseSoseiRate(readSoseiRowValue(row, mainRateCol));
                if (mainRate !== null) {
                    partTotals.set(partCode, (partTotals.get(partCode) || 0) + mainRate);
                }
            }

            const subCode = readSoseiRowValue(row, subCol);
            if (subCode === '') return;

            const mainCode = readSoseiRowValue(row, mainCol);
            if (mainCode === '') return;
            const subRate = parseSoseiRate(readSoseiRowValue(row, subRateCol));
            if (subRate === null) return;
            subTotals.set(mainCode, (subTotals.get(mainCode) || 0) + subRate);
        });
        return Array.from(partTotals.values()).some((total) => total > 100)
            || Array.from(subTotals.values()).some((total) => total > 100);
    }
    // グループ単位でサブ組成の混率を100%まで埋め、空行は必要に応じて除去する。
    function calculateSoseiRows(rows, meta) {
        const nextRows = DeepCopy(Array.isArray(rows) ? rows : []);
        const groups = buildSoseiGroupsByInputOrder(nextRows, meta);
        const removeRows = new Set();
        groups.forEach((group) => {
            const groupRows = Array.isArray(group.rows) ? group.rows : [];
            if (groupRows.length === 0) return;
            const baseRow = groupRows[0];
            const defaultSub = resolveDefaultSubSosei(baseRow, meta);
            const subRows = groupRows.filter((row) => !isBlankSoseiSubFiber(row, meta));
            const blankSubRows = groupRows.filter((row) => isBlankSoseiSubFiber(row, meta));
            const blankSubRowWithRate = blankSubRows.find((row) => !isBlankSoseiRate(row, meta.subRateCol));
            const applyDefaultSubToBlankRow = (fallbackRate) => {
                const targetRow = blankSubRowWithRate || blankSubRows[0] || baseRow;
                const enteredRate = parseSoseiRate(readSoseiRowValue(targetRow, meta.subRateCol));
                const rate = enteredRate !== null && enteredRate !== 0 ? enteredRate : fallbackRate;
                applyDefaultSubSoseiToRow(targetRow, meta, defaultSub, rate);
                return { targetRow, rate };
            };
            if (subRows.length === 0) {
                const applied = applyDefaultSubToBlankRow(100);
                if (Number(DicVal(applied, 'rate', 0)) >= 100) {
                    blankSubRows.forEach((row) => {
                        if (row !== DicVal(applied, 'targetRow', null) && isBlankSoseiDisplayRow(row, meta)) removeRows.add(row);
                    });
                }
                return;
            }
            const enteredRows = subRows.filter((row) => !isBlankSoseiRate(row, meta.subRateCol));
            const blankRateRows = subRows.filter((row) => isBlankSoseiRate(row, meta.subRateCol));
            const enteredTotal = enteredRows.reduce((sum, row) => sum + (parseSoseiRate(readSoseiRowValue(row, meta.subRateCol)) || 0), 0);
            const remaining = Math.max(0, 100 - enteredTotal);
            if (blankRateRows.length !== 0) {
                if (remaining <= 0) {
                    blankRateRows.forEach((row) => {
                        if (isSoseiRemainderCandidateRow(row, meta)) removeRows.add(row);
                    });
                    return;
                }
                const rates = splitSoseiRate(remaining, blankRateRows.length, meta.subRateDecimalPlaces);
                blankRateRows.forEach((row, index) => {
                    writeSoseiRowField(row, meta.subRateCol, formatSoseiRate(rates[index], meta.subRateDecimalPlaces));
                });
                return;
            }
            if (blankSubRows.length !== 0) {
                if (remaining <= 0) {
                    blankSubRows.forEach((row) => {
                        if (isBlankSoseiDisplayRow(row, meta)) removeRows.add(row);
                    });
                    return;
                }
                applyDefaultSubToBlankRow(remaining);
                return;
            }
            if (remaining <= 0) return;
            addSoseiRateToRow(subRows[subRows.length - 1], meta, remaining);
        });
        const resultRows = removeRows.size === 0 ? nextRows : nextRows.filter((row) => !removeRows.has(row));
        return { rows: resultRows, groupCount: groups.length };
    }

    return {
        normalizeSoseiColumnId,
        getSoseiPanelColumnInfo,
        readSoseiRowValue,
        readSoseiRowName,
        writeSoseiRowField,
        writeSoseiMasterValue,
        parseSoseiRate,
        isBlankSoseiRate,
        isBlankSoseiSubFiber,
        isBlankSoseiDisplayValue,
        isBlankSoseiDisplayRow,
        isSoseiRemainderCandidateRow,
        formatSoseiRate,
        splitSoseiRate,
        readSoseiMasterCacheName,
        resolveDefaultSubSosei,
        applyDefaultSubSoseiToRow,
        addSoseiRateToRow,
        sameSoseiParentIdentity,
        buildSoseiGroupsByInputOrder,
        hasDuplicateSoseiFiberRows,
        hasSoseiRateOverLimit,
        calculateSoseiRows
    };
}
